import {
  OAuth2Client,
  BrowserOAuth2TokenStorage,
  type OAuth2Tokens,
  type OAuth2TokenStorage
} from '@docyrus/api-client';

import { type DocyrusAuthConfig } from '../types';

import {
  DEFAULT_API_URL,
  DEFAULT_OAUTH_SCOPES,
  DEFAULT_CALLBACK_PATH,
  REDIRECT_RETURN_KEY
} from '../constants';

/**
 * Standalone OAuth2 authorization code flow with PKCE via page redirect.
 * Wraps api-client's OAuth2Client and BrowserOAuth2TokenStorage.
 */
export class StandaloneOAuth2Auth {
  private oauth2Client: OAuth2Client;
  private storage: OAuth2TokenStorage;
  private callbackPath: string;
  constructor(config: DocyrusAuthConfig = {}) {
    const apiUrl = config.apiUrl ?? DEFAULT_API_URL;

    if (!config.clientId) {
      throw new Error(
        'DocyrusAuthProvider: "clientId" is required. '
        + 'Set it via the clientId prop or the VITE_DOCYRUS_CLIENT_ID / NEXT_PUBLIC_DOCYRUS_CLIENT_ID environment variable.'
      );
    }
    const { clientId } = config;

    const scopes = config.scopes ?? DEFAULT_OAUTH_SCOPES;

    this.callbackPath = config.callbackPath ?? DEFAULT_CALLBACK_PATH;
    const redirectUri = config.redirectUri
      ?? `${window.location.origin}${this.callbackPath}`;

    this.storage = config.tokenStorage ?? new BrowserOAuth2TokenStorage(
      window.localStorage,
      config.storageKeyPrefix ?? 'docyrus_oauth2'
    );

    this.oauth2Client = new OAuth2Client({
      baseURL: apiUrl,
      clientId,
      redirectUri,
      defaultScopes: scopes,
      usePKCE: true,
      tokenStorage: this.storage
    });
  }
  /**
   * Check if the current page URL is the OAuth2 callback.
   * Called on app initialization to detect returning from the auth server.
   */
  isCallbackUrl(): boolean {
    return window.location.pathname === this.callbackPath
      && (window.location.search.includes('code=')
        || window.location.search.includes('error='));
  }
  /**
   * Initiate the OAuth2 authorization code flow.
   * Stores the current URL for post-auth redirect, then navigates
   * the page to the authorization endpoint.
   *
   * PKCE state (codeVerifier, state) is stored in localStorage
   * by the OAuth2Client and survives the page redirect.
   */
  async initiateLogin(): Promise<void> {
    window.localStorage.setItem(REDIRECT_RETURN_KEY, window.location.href);

    const { url } = await this.oauth2Client.getAuthorizationUrl();

    window.location.href = url;
  }
  /**
   * Get the authorization URL without navigating.
   * Useful for Electron apps that open the URL in an external browser.
   * PKCE state is stored in storage just like initiateLogin().
   */
  async getAuthorizationUrl(): Promise<string> {
    window.localStorage.setItem(REDIRECT_RETURN_KEY, window.location.href);

    const { url } = await this.oauth2Client.getAuthorizationUrl();

    return url;
  }
  /**
   * Handle the OAuth2 callback URL.
   * Reads PKCE state from localStorage, validates the state param,
   * exchanges the authorization code for tokens, and stores them.
   */
  async handleCallback(): Promise<OAuth2Tokens> {
    return this.oauth2Client.handleCallback(window.location.href);
  }
  /**
   * Get the URL the user was on before the OAuth redirect.
   * Clears the stored value after retrieval.
   */
  getReturnUrl(): string | null {
    const url = window.localStorage.getItem(REDIRECT_RETURN_KEY);

    window.localStorage.removeItem(REDIRECT_RETURN_KEY);

    return url;
  }
  /** Get currently stored tokens (e.g., on page reload). */
  async getStoredTokens(): Promise<OAuth2Tokens | null> {
    return this.oauth2Client.getTokens();
  }
  /** Persist externally obtained tokens for immediate authenticated use. */
  async setTokens(tokens: OAuth2Tokens): Promise<void> {
    await Promise.resolve(this.storage.setTokens(tokens));
  }
  /** Check if the stored token is expired (with 60s buffer). */
  async isTokenExpired(): Promise<boolean> {
    return this.oauth2Client.isTokenExpired();
  }
  /** Refresh the access token using the stored refresh token. */
  async refreshToken(): Promise<OAuth2Tokens> {
    return this.oauth2Client.refreshAccessToken();
  }
  /** Get a valid access token, refreshing if needed. */
  async getValidAccessToken(): Promise<string> {
    return this.oauth2Client.getValidAccessToken();
  }
  /** Logout: revoke token and clear storage. */
  async logout(): Promise<void> {
    return this.oauth2Client.logout();
  }
}
